import React, { useCallback, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import styled from 'styled-components';

import { faUserEdit } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  DialogContent,
  Grid,
  ListItemText,
  TextField
} from '@mui/material';
import { ToggleButton, ToggleButtonGroup } from '@mui/lab';

import {
  FormElement,
  RequiredSelectHack, StyledListItem
} from 'components';
import debounce from 'debounce-promise';
import { FormHelper, UserHelper, translate } from 'utils';
import { APPLICATION_ROLES } from 'utils/constants';

import { observer } from 'mobx-react-lite';

import { useStores } from 'hooks';
import AsyncSelect from 'react-select/async';
import { CompanyService } from 'services';
import { CustomActionButtons } from './_CustomActionButtons';
import { HeaderModal } from './_HeaderModal';

function ensureString(value: string | object): string {
  return typeof value === 'string' ? value : String(value);
}

const UpdateUserForm = styled.form`
  .MuiToggleButton-root,
  .MuiToggleButton-root:hover {
    background-color: var(--white);
  }

  .MuiToggleButton-root.Mui-selected:first-child,
  .MuiToggleButton-root.Mui-selected:first-child:hover {
    background-color: var(--success-color);
  }

  .MuiToggleButton-root.Mui-selected:last-child,
  .MuiToggleButton-root.Mui-selected:last-child:hover {
    background-color: var(--error-color);
  }

  @media(min-width: 768px) {
    width: 600px;
  }
`;


interface UpdateUserModalProps {
  closeOnSubmit?: boolean;
  defaultValues: {
    accountActivated: boolean;
    accountId: string;
    canEditEmail: boolean;
    login: string;
    modelId: string;
    person: {
      address: {
        address1: string;
        address2: string;
        city: string;
        postalCode: string;
      };
      contact: {
        contactEmails: string;
        firstContactEmail: string;
        phoneNumber: string;
        technicalEmail: string;
      };
      firstName: string;
      lastName: string;
      mainCompany: any;
      occupation: string;
      validated: boolean;
    };
    personId: string;
  };
  onClose: () => void;
  onConfirm: (userData: any) => void;
}

const validateEmail = (value: string): boolean | string => {
  const result = FormHelper.validateEmail(value);
  return typeof result === 'string' ? result : true;
};

const validateEmails = (value: string): boolean | string => {
  const result = FormHelper.validateEmails(value);
  return typeof result === 'string' ? result : true;
};

export const UpdateUserModal = observer(({
                                           onClose, onConfirm, closeOnSubmit = true, defaultValues
                                         }: UpdateUserModalProps) => {
  const { userStore } = useStores();
  const { scope } = userStore;
  const [selectedCompany, setSelectedCompany] = useState(defaultValues?.person?.mainCompany);


  const {
 control, handleSubmit, formState: { errors }, watch, setValue
} = useForm({
    defaultValues: {
      accountId: defaultValues.accountId,
      accountActivated: defaultValues.accountActivated,
      login: defaultValues.login || '',
      firstName: defaultValues.person.firstName || '',
      lastName: defaultValues.person.lastName || '',
      firstContactEmail: defaultValues.person.contact.firstContactEmail || '',
      phoneNumber: defaultValues.person.contact.phoneNumber || '',
      occupation: defaultValues.person.occupation || '',
      address1: defaultValues.person.address.address1 || '',
      address2: defaultValues.person.address.address2 || '',
      postalCode: defaultValues.person.address.postalCode || '',
      city: defaultValues.person.address.city || '',
      country: UserHelper.getDefaultCountry(defaultValues ? defaultValues.person?.address : null),
      email: defaultValues.person.contact.technicalEmail || '',
      contactEmails: defaultValues.person.contact.contactEmails || ''
    }
  });

  // Watch values for conditional validation
  const formValues = watch();
  const accountActivated = watch('accountActivated');

  useEffect(() => {
    if (selectedCompany) {
      return;
    }
    if (scope?.company) {
      setSelectedCompany(scope?.company);
    }
    // eslint-disable-next-line
  }, []);

  const handleAddCompany = useCallback((value: any) => {
    setSelectedCompany(value);
  }, []);

  const getAsyncCompaniesOptions = useCallback((inputValue: string) => new Promise(resolve => {
    CompanyService
        .getCompanyOptions({ freeSearch: inputValue })
        .then(response => resolve(response));
  }), []);

  const debouncedCompaniesLoadOptions = debounce(getAsyncCompaniesOptions, 500);

  interface CustomOptionProps {
    data: {
      label: string;
    };
    innerProps: any;
  }

  const CustomOption = ({ data, innerProps }: CustomOptionProps) => (
    <StyledListItem {...innerProps}>
      <ListItemText primary={data.label} />
    </StyledListItem>
  );

  // Only admin users can activate or deactivate users and cannot deactivate their own account
  const canManageActivation = () => !(!UserHelper.hasAccessRight([APPLICATION_ROLES.ADMIN]) || defaultValues.accountId === scope.accountId);

  // Check if fields are required based on other field values
  const isAddressFieldRequired = useCallback((fieldsToCheck: string[]) => FormHelper.isFieldRequired({
      fieldsToCheck
    }), []);

  const onSubmit = useCallback((data: any) => {
    userStore.updateStart();

    onConfirm({
      accountId: defaultValues.accountId,
      personId: defaultValues.personId,
      modelId: defaultValues.modelId,
      accountActivated: data.accountActivated,
      login: data.login,
      person: {
        ...defaultValues.person,
        address: {
          address1: data.address1,
          address2: data.address2,
          postalCode: data.postalCode,
          city: data.city
        },
        birthDate: data.birthDate,
        firstName: data.firstName,
        lastName: data.lastName,
        mainCompany: selectedCompany,
        contact: {
          technicalEmail: data.email,
          contactEmails: data.contactEmails,
          phoneNumber: data.phoneNumber
        },
        occupation: data.occupation
      }
    });

    if (closeOnSubmit) {
      onClose();
    }
  }, [userStore, closeOnSubmit, onClose, onConfirm, defaultValues, selectedCompany]);

  return (
    <UpdateUserForm autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
      <HeaderModal onClose={onClose}>
        <Grid alignItems="center" container justifyContent="space-between">
          <div>
            <FontAwesomeIcon icon={faUserEdit} />
            {`${ensureString(translate('modalUserUpdate.editUser'))} : ${formValues.login}`}
          </div>
        </Grid>
      </HeaderModal>

      <DialogContent style={{ paddingTop: '10px' }}>
        <Grid container direction="column">
          <FormElement label={ensureString(translate('pageUserDetail.myInfos'))}>
            <Grid container spacing={2}>
              <Grid item sm={6} xs={12}>
                <Controller
                      control={control}
                      name="lastName"
                      render={({ field }) => (
                        <TextField
                              {...field}
                              autoComplete="new-password"
                              error={!!errors.lastName}
                              label={ensureString(translate('common.lastName'))}
                              name="lastName"
                              required
                          />
                      )}
                      rules={{ required: true }}
                  />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Controller
                      control={control}
                      name="firstName"
                      render={({ field }) => (
                        <TextField
                              {...field}
                              autoComplete="new-password"
                              error={!!errors.firstName}
                              label={ensureString(translate('common.firstName'))}
                              name="firstName"
                              required
                          />
                      )}
                      rules={{ required: true }}
                  />
              </Grid>
            </Grid>
            <Grid container>
              <Controller
                    control={control}
                    name="occupation"
                    render={({ field }) => (
                      <TextField
                            {...field}
                            autoComplete="new-password"
                            error={!!errors.occupation}
                            inputProps={{ maxLength: 200 }}
                            label={ensureString(translate('common.occupation'))}
                            name="occupation"
                        />
                    )}
                />
            </Grid>
          </FormElement>
          <FormElement label={ensureString(translate('common.contact'))}>
            <Grid container spacing={2}>
              <Grid item sm={6} xs={12}>
                <Controller
                      control={control}
                      name="contactEmails"
                      render={({ field }) => (
                        <TextField
                              {...field}
                              autoComplete="new-password"
                              disabled={!defaultValues.canEditEmail}
                              error={!!errors.contactEmails}
                              helperText={errors.contactEmails?.message}
                              label={ensureString(translate('common.contactEmails.modal'))}
                              name="contactEmails"
                          />
                      )}
                      rules={{
                        validate: validateEmails
                      }}
                  />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Controller
                      control={control}
                      name="phoneNumber"
                      render={({ field }) => (
                        <TextField
                              {...field}
                              autoComplete="new-password"
                              error={!!errors.phoneNumber}
                              label={ensureString(translate('common.phone'))}
                              name="phoneNumber"
                              type="tel"
                          />
                      )}
                  />
              </Grid>
            </Grid>
          </FormElement>
          <FormElement label={ensureString(translate('common.address'))}>
            <Grid container spacing={2}>
              <Grid item sm={6} xs={12}>
                <Controller
                      control={control}
                      name="address1"
                      render={({ field }) => (
                        <TextField
                              {...field}
                              autoComplete="new-password"
                              error={!!errors.address1}
                              label={ensureString(translate('common.address'))}
                              name="address1"
                              required={isAddressFieldRequired([formValues.postalCode, formValues.city])}
                          />
                      )}
                      rules={{
                        required: isAddressFieldRequired([formValues.postalCode, formValues.city])
                      }}
                  />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Controller
                      control={control}
                      name="address2"
                      render={({ field }) => (
                        <TextField
                              {...field}
                              autoComplete="new-password"
                              error={!!errors.address2}
                              label={ensureString(translate('common.address2'))}
                              name="address2"
                          />
                      )}
                  />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item sm={6} xs={12}>
                <Controller
                      control={control}
                      name="postalCode"
                      render={({ field }) => (
                        <TextField
                              {...field}
                              autoComplete="new-password"
                              error={!!errors.postalCode}
                              label={ensureString(translate('common.postalCode'))}
                              name="postalCode"
                              required={isAddressFieldRequired([formValues.address1, formValues.city])}
                              style={{ maxWidth: '100px' }}
                          />
                      )}
                      rules={{
                        required: isAddressFieldRequired([formValues.address1, formValues.city])
                      }}
                  />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Controller
                      control={control}
                      name="city"
                      render={({ field }) => (
                        <TextField
                              {...field}
                              autoComplete="new-password"
                              error={!!errors.city}
                              label={ensureString(translate('common.city'))}
                              name="city"
                              required={isAddressFieldRequired([formValues.address1, formValues.postalCode])}
                          />
                      )}
                      rules={{
                        required: isAddressFieldRequired([formValues.address1, formValues.postalCode])
                      }}
                  />
              </Grid>
              {/* This input is only used for forcing disabling autocomplete in chrome browser */}
              <input name="fakeInputForForcingDisablingAutocompleteChrome4" style={{ display: 'none' }} type="text" />
            </Grid>
          </FormElement>

          {canManageActivation() && (
            <FormElement label={ensureString(translate('common.userAccount'))}>
              <Grid container spacing={2}>
                <Grid item>
                  <div>
                    <Controller
                            control={control}
                            name="accountActivated"
                            render={({ field: { value, onChange } }) => (
                              <ToggleButtonGroup
                                    aria-label={ensureString(translate('common.userAccount'))}
                                    exclusive
                                    size="small"
                                    value={value}
                                    onChange={(event, newValue) => {
                                      if (newValue === null) return;
                                      onChange(newValue);
                                    }}
                                >
                                <ToggleButton
                                      aria-label={ensureString(translate('common.activated'))}
                                      value={true}
                                  >
                                  {ensureString(translate('common.activated'))}
                                </ToggleButton>
                                <ToggleButton
                                      aria-label={ensureString(translate('common.deactivated'))}
                                      value={false}
                                  >
                                  {ensureString(translate('common.deactivated'))}
                                </ToggleButton>
                              </ToggleButtonGroup>
                            )}
                        />
                  </div>
                </Grid>
                {accountActivated && (
                <Grid container item spacing={2}>
                  <Grid item sm={6} xs={12}>
                    <Controller
                                control={control}
                                name="login"
                                render={({ field }) => (
                                  <TextField
                                        {...field}
                                        autoComplete="new-password"
                                        disabled={defaultValues.accountActivated}
                                        error={!!errors.login}
                                        label={ensureString(translate('common.login'))}
                                        name="login"
                                        required
                                    />
                                )}
                                rules={{ required: true }}
                            />
                  </Grid>
                  <Grid item sm={6} xs={12}>
                    <Controller
                                control={control}
                                name="email"
                                render={({ field }) => (
                                  <TextField
                                        {...field}
                                        autoComplete="new-password"
                                        disabled={!defaultValues.canEditEmail}
                                        error={!!errors.email}
                                        helperText={errors.email?.message}
                                        label={ensureString(translate('common.userModal.technicalEmail'))}
                                        name="email"
                                        required
                                        type="email"
                                    />
                                )}
                                rules={{
                                  required: true,
                                  validate: validateEmail
                                }}
                            />
                  </Grid>
                </Grid>
                    )}
              </Grid>
            </FormElement>
            )}

          {(!defaultValues.person?.mainCompany || UserHelper.hasAccessRight([APPLICATION_ROLES.ADMIN])) && (
            <FormElement label={`${ensureString(translate('common.company'))} *`}>
              <AsyncSelect
                      cacheOptions
                      closeMenuOnSelect
                      components={{ Option: CustomOption }}
                      defaultOptions
                      isClearable
                      loadingMessage={() => ensureString(translate('common.loading'))}
                      loadOptions={inputValue => debouncedCompaniesLoadOptions(inputValue)}
                      menuPortalTarget={document.body}
                      name="companies"
                      noOptionsMessage={() => ensureString(translate('errors.noOptions'))}
                      placeholder={ensureString(translate('common.searchByInstitution'))}
                      styles={{
                        menu: base => ({ ...base, zIndex: 2000 }),
                        menuPortal: basePortal => ({ ...basePortal, zIndex: 2000 })
                      }}
                      value={selectedCompany}
                      onChange={handleAddCompany}
                  />
              <RequiredSelectHack value={selectedCompany || ''} />
            </FormElement>
            )}

        </Grid>
      </DialogContent>

      <CustomActionButtons
            isLoading={userStore.isUpdating}
            isValidated={defaultValues.person.validated}
            onClose={onClose}
        />
    </UpdateUserForm>
  );
});
